import React from "react";
import { Card, CtaBand, Section, SectionHead } from "../components/ui";
import * as _ from "lodash";
import { DATA_STORE_IMG_PATH } from "../content/site";

export const metadata = { title: "Partners" };

const BENEFITS = [
	[
		"Your data joins a catalog buyers shop across",
		"Buyers come to ch-aviation to source several feeds at once, all built to work together. Your data sits right among them, discovered and chosen as part of the same trip. That's reach a feed on its own can't match.",
	],
	[
		"Your data fits in automatically",
		"We add ch-aviation identifiers to your data, so it joins cleanly to every other feed in our catalog. That's exactly why buyers want to source multiple feeds from us, and why yours becomes more valuable once it's part of the collection.",
	],
	[
		"Pure upside, no exclusivity",
		"We're an additional channel, never a replacement. Keep selling your data directly, on other platforms, however you do business today. We don't ask for exclusivity. We simply put your data in front of more buyers.",
	],
	[
		"Completely hassle-free",
		"No storefront to run, no listings to manage, no customers to support. You supply and maintain your data. We handle standardization, distribution, billing, and the customer relationship.",
	],
];

const PARTNERS = [
	{
		title: "OAG",
		description: "Schedules and capacity data",
		src: `${DATA_STORE_IMG_PATH}/oag-logo.svg`,
		url: "https://www.oag.com/",
	},
	{
		title: "Spire",
		description: "Satellite flight tracking",
		src: `${DATA_STORE_IMG_PATH}/spire-aviation-logo.png`,
		url: "https://spire.com/aviation/",
	},
	{
		title: "Flight Safety Foundation",
		description: "Safety and accident data",
		src: `${DATA_STORE_IMG_PATH}/fsf-logo.png`,
		url: "https://flightsafety.org/",
	},
	{
		title: "Collateral Verifications",
		description: "Aircraft appraisals and valuations",
		src: `${DATA_STORE_IMG_PATH}/collateral-verifications-logo.jpg`,
		url: "https://www.cvllc.net/",
	},
];

const COMMERCIAL = [
	[
		"Revenue share",
		"You earn a share of the revenue from every sale that includes your data, based on commercial terms we agree up front. You'll know your share before anything goes live.",
	],
	[
		"Pricing",
		"We set the customer-facing price, based on the commercials we've agreed with you. This keeps pricing consistent across our catalog, so buyers see one coherent set of feeds rather than a patchwork.",
	],
	[
		"No exclusivity",
		"We never ask for exclusivity. Keep selling your data directly, on other platforms, and through your existing channels. We're simply an additional route to more buyers.",
	],
	[
		"Your brand, your choice",
		"Your data can be fully white-labeled as part of ch-aviation's feeds, or clearly attributed to you. The same applies when a customer builds a product on the data. You decide what works for you.",
	],
	[
		"We handle the customer, with full transparency",
		"Buyers contract with ch-aviation, and we handle billing, delivery, and support. But we don't keep customers from you. You'll always know who bought your data and who the contact is, and you're free to talk with them directly.",
	],
];

const STEPS = [
	[
		"1",
		"Get in touch",
		"Tell us about your data and what you'd like to do. We'll figure out together whether it's a fit.",
	],
	[
		"2",
		"Agree terms",
		"We agree the commercial terms, branding approach, and delivery schedule. You'll know exactly how the partnership works before we build anything.",
	],
	[
		"3",
		"We integrate your data",
		"We standardize your data to our format, add our identifiers, and build it into ch-aviation feeds. We handle the technical work.",
	],
	[
		"4",
		"Go live",
		"Your data goes live in the catalog, reaching buyers sourcing feeds from across ch-aviation. Your data flows at scheduled intervals, and you're paid on the terms we agreed.",
	],
];

const FAQ = [
	[
		"What kind of data are you looking for?",
		"Any aviation data that complements our catalog. If it's aviation-related and well-maintained, we'd like to hear about it.",
	],
	[
		"What format does my data need to be in?",
		"Whatever format you already work with. We handle standardizing it to our structure and adding our identifiers.",
	],
	[
		"How often do I need to send updates?",
		"On whatever schedule suits your data, delivered at pre-scheduled intervals. We agree the cadence up front.",
	],
	[
		"Do I keep selling my data elsewhere?",
		"Yes. We never ask for exclusivity. Keep selling direct, on other platforms, and through your existing channels.",
	],
	[
		"Who owns my data?",
		"You do. You always own your data. We license it under the terms we agree, standardize it, and include it in our feeds.",
	],
	[
		"Will customers know the data is mine?",
		"That's your choice. White-label it as part of ch-aviation's feeds, or have it clearly attributed to you.",
	],
	[
		"What if I want to end the partnership?",
		"We'll agree notice terms up front. If you leave, we stop including your data according to those terms.",
	],
	[
		"How do I get paid?",
		"A revenue share on every sale that includes your data, paid on the schedule we agree, with regular reporting.",
	],
];

export default function Partners() {
	return (
		<>
			<section className="section-hero tw-bg-white">
				<div className="container">
					<span className="eyebrow">Partner programme</span>
					<h1 className="h2 tw-my-5 tw-max-w-6xl">
						Supply the data. We do the rest.
					</h1>
					<p className="lead tw-max-w-2xl">
						ch-aviation builds and sells the aviation data feeds
						buyers rely on. Partner with us and your data becomes
						part of them, reaching buyers who come here to source
						feeds that all work together. No storefront, no
						listings, no customer management. Just your data in our
						feeds.
					</p>
				</div>
			</section>

			<Section variant="surface">
				<SectionHead title="You supply data. We handle everything else." />
				<div className="cards-list tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
					<div className="typography card card--no-bg">
						<h3 className="h5">You</h3>
						<ul>
							{[
								"Supply your data in whatever format you already have",
								"Keep it updated on your normal schedule, delivered at pre-scheduled intervals",
							].map((t) => (
								<li key={t}>{t}</li>
							))}
						</ul>
						<div className="tw-mt-2.5 tw-text-[14px] tw-font-semibold tw-text-ink">
							That&apos;s it.
						</div>
					</div>
					<div className="typography card card--no-bg">
						<h3 className="h5">ch-aviation</h3>
						<ul>
							{[
								"Standardize your data to our common format",
								"Add our identifiers so it joins the rest of our feeds",
								"Build it into ch-aviation data feeds",
								"Sell to buyers sourcing multiple feeds from one place",
								"Handle billing, contracts, delivery, and support",
								"Pay you based on the terms we agreed",
							].map((t) => (
								<li key={t}>{t}</li>
							))}
						</ul>
					</div>
				</div>
			</Section>

			<Section>
				<SectionHead title="Why partner with us." />
				<div className="cards-list tw-grid tw-grid-cols-1 md:tw-grid-cols-2">
					{BENEFITS.map(([t, d]) => (
						<Card key={t} title={t} body={d} />
					))}
				</div>
			</Section>

			<Section variant="surface">
				<SectionHead center title="Some of our data partners." />
				<div className="cards-list logos-list tw-grid tw-grid-cols-1 md:tw-grid-cols-4">
					{PARTNERS.map(({ title, description, src, url }) => (
						<div
							key={_.camelCase(title)}
							className="card tw-text-center"
						>
							<div className="badge tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-line tw-text-muted">
								<a
									href={url}
									target="_blank"
									rel="noopener noreferrer"
								>
									{src ? (
										<img src={src} alt={title} />
									) : (
										title
									)}
								</a>
							</div>
							<p className="tw-text-[13px]">{description}</p>
						</div>
					))}
				</div>
			</Section>

			<Section>
				<SectionHead title="The commercial model." />
				<ul className="list-vertical">
					{COMMERCIAL.map(([t, d]) => (
						<li key={t} className="tw-tw-border-b tw-border-line">
							<h3 className="h6">{t}</h3>
							<p className="tw-text-[14px]">{d}</p>
						</li>
					))}
				</ul>
			</Section>

			<Section variant="surface">
				<SectionHead title="How to become a partner." />
				<div className="grid tw-grid-cols-1 tw-gap-5 md:tw-grid-cols-4">
					{STEPS.map(([n, t, d]) => (
						<div key={n}>
							<div className="tw-mb-3.5 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-red tw-text-[15px] tw-font-bold tw-text-white">
								{n}
							</div>
							<h3 className="h6">{t}</h3>
							<p className="tw-text-[13.5px] tw-leading-relaxed">
								{d}
							</p>
						</div>
					))}
				</div>
			</Section>

			<Section>
				<SectionHead title="Frequently asked questions." />
				<ul className="list-vertical tw-grid tw-grid-cols-1 tw-gap-x-11 md:tw-grid-cols-2">
					{FAQ.map(([q, a]) => (
						<li key={q} className="tw-border-b tw-border-line">
							<h3 className="h6">{q}</h3>
							<p className="tw-text-[13.5px]">{a}</p>
						</li>
					))}
				</ul>
			</Section>

			<CtaBand
				title="Think your data belongs in the Data Store?"
				body="Tell us about it. We'll figure out together whether it's a fit."
				button="Get in touch"
			/>
		</>
	);
}
